import { useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  type NotificationPreferenceKey,
  type NotificationPreferences as NotificationPreferencesValue,
  unrecognizedKeysFromError,
} from '@/lib/api/domains/notification-preferences';
import type { UserId } from '@/lib/api/ids';
import {
  NOTIFICATION_PREFERENCE_LABELS,
  NOTIFICATION_PREFERENCE_ORDER,
} from '../schemas/notification-preferences';
import { notificationPreferencesQuery } from '../api/notification-preference-queries';
import { useUpdateNotificationPreferences } from '../api/notification-preference-mutations';

/*
 * The client portal's notification-preference screen (F02 R25/R26/R35) where a
 * client user sets THEIR OWN four choices (AC-c4ed6685), held per person
 * (AC-3d6561d1).
 *
 *   - the four GOVERNED keys (AC-09826b9a) render as labelled toggles, in the
 *     registry's display order. The server has already applied DEFAULT-ON on
 *     read (AC-f00ecffd), so the billing notification shows ON for a new client
 *     user (AC-e0a73de2) unless turned off (AC-a538e368) — the screen reflects
 *     the server's values and never computes a default itself;
 *   - Save PUTs the choices via `useUpdateNotificationPreferences`, one answer
 *     per key (AC-dd4c1e06), mutate → invalidate (no optimistic write);
 *   - a whole-submission refusal for an unknown key is surfaced where the person
 *     works, NAMING the unrecognized keys (`unrecognizedKeysFromError`;
 *     AC-8ddebd3b, AC-c7c5f9af). Nothing is shown as saved — the server changed
 *     nothing, so the screen re-reads the unchanged values.
 *
 * The server is the authority on the governed registry, all-or-nothing and
 * default-on; this screen is the courtesy half. PII: the four keys are governed
 * ids, not names/emails; the request targets the account by id (AGENTS.md — ids
 * only).
 */
export function NotificationPreferences({ userId }: { userId: UserId }) {
  const query = useQuery(notificationPreferencesQuery(userId));
  const update = useUpdateNotificationPreferences(userId);
  const headingId = useId();

  // Local working copy of the toggles, seeded from the server's values and kept
  // in sync whenever the server values change (first load, and after a save
  // invalidates and re-reads). The server owns the truth; this is the in-flight
  // edit the person is making before Save.
  const [draft, setDraft] = useState<NotificationPreferencesValue | null>(null);
  const serverValues = query.data;
  useEffect(() => {
    if (serverValues) setDraft(serverValues);
  }, [serverValues]);

  // The unrecognized key names from a whole-submission refusal, surfaced where
  // the person works (AC-c7c5f9af). Any other failure is the generic refusal.
  const unrecognized = update.isError
    ? unrecognizedKeysFromError(update.error)
    : null;

  // Split "loading" from "error" (finding 11): a failed read must NOT render a
  // permanent "Loading…". When the read errors, show a retryable error state
  // (generic failure + a retry that refetches); keep "Loading…" only while the
  // read is genuinely pending. The 401/403 interceptor is the backstop — no
  // component-level status check here.
  if (!draft && query.isError) {
    return (
      <section aria-labelledby={headingId} className="flex flex-col gap-3">
        <h2 id={headingId} className="text-base font-medium">
          Notification preferences
        </h2>
        <Alert variant="destructive" role="alert">
          <AlertTitle>Your preferences could not be loaded</AlertTitle>
          <AlertDescription>
            Something went wrong. Please try again.
          </AlertDescription>
        </Alert>
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </div>
      </section>
    );
  }

  if (!draft) {
    return (
      <section aria-labelledby={headingId}>
        <h2 id={headingId} className="text-base font-medium">
          Notification preferences
        </h2>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }

  function setKey(key: NotificationPreferenceKey, enabled: boolean) {
    setDraft((prev) => (prev ? { ...prev, [key]: enabled } : prev));
  }

  function onSave() {
    if (!draft) return;
    update.mutate({ choices: draft });
  }

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h2 id={headingId} className="text-base font-medium">
        Notification preferences
      </h2>

      {update.isError ? (
        <Alert variant="destructive">
          <AlertTitle>Your preferences were not saved</AlertTitle>
          <AlertDescription>
            {unrecognized && unrecognized.length > 0
              ? `These choices were not recognized and nothing was saved: ${unrecognized.join(', ')}.`
              : 'Something went wrong and nothing was saved. Please try again.'}
          </AlertDescription>
        </Alert>
      ) : null}

      <ul className="flex flex-col gap-3">
        {NOTIFICATION_PREFERENCE_ORDER.map((key) => {
          const labelId = `${headingId}-${key}`;
          return (
            <li key={key} className="flex items-center justify-between gap-4">
              <Label id={labelId} htmlFor={labelId + '-switch'}>
                {NOTIFICATION_PREFERENCE_LABELS[key]}
              </Label>
              <Switch
                id={labelId + '-switch'}
                aria-labelledby={labelId}
                checked={draft[key]}
                onCheckedChange={(checked) => setKey(key, checked)}
              />
            </li>
          );
        })}
      </ul>

      <div className="flex justify-end">
        <Button type="button" onClick={onSave} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </section>
  );
}
